<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      
        <template #header>
          <div class="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
            <h2 class="text-lg font-semibold">{{ isMobile() ? '反向计算' : `反向计算（${method==='EPI'?'等额本息':'等额本金'}）` }}</h2>
            <div class="flex items-center gap-2 flex-wrap">
              <el-segmented class="EPI mobile-wrap w-full sm:w-auto" v-model="method" :options="methodOptions" />
              <el-segmented class="mobile-wrap w-full sm:w-auto" v-model="unknown" :options="unknownOptions" />
              <el-dropdown class="w-full sm:w-auto" @command="onSelectExample">
                <el-button size="small" plain>
                  示例方案选择
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-for="ex in examples" :key="ex.key" :command="ex.key">
                      {{ ex.title }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </template>
      <el-alert type="info" :closable="false" class="mb-3" title="用途：反向求解未知量（金额/利率/期数/月供）中的一个，适合做预算、核对银行方案或做敏感性分析。" />
  
    <!-- 示例方案弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px">
      <div class="space-y-4">
        <el-alert type="info" :closable="false" show-icon>
          <template #title>
            {{ dialogTitle }}（示例）
          </template>
          <div class="text-sm text-gray-600 leading-relaxed" v-html="dialogDesc"></div>
        </el-alert>

        <el-form label-position="top" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <el-form-item class="md:col-span-2" label="未知量">
            <el-segmented v-model="exUnknown" :options="unknownOptions" />
          </el-form-item>
          <el-form-item :label="`贷款金额（${symbol}）`" v-if="exUnknown!=='amount'">
            <el-input-number v-model="exAmountUI" :min="0" :step="1000" :precision="0" controls-position="right" class="w-full" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="年化利率（%）" v-if="exUnknown!=='annualRatePct'">
            <el-input-number v-model="exForm.annualRatePct" :min="0" :step="0.01" :precision="2" controls-position="right" class="w-full" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数（月）" v-if="exUnknown!=='months'">
            <el-input-number v-model="exForm.months" :min="1" :step="1" :precision="0" controls-position="right" class="w-full" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="`月供（${symbol}）`" v-if="exUnknown!=='pmt'">
            <el-input-number v-model="exPmtUI" :min="0" :step="100" :precision="2" controls-position="right" class="w-full" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PMT）</div>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <el-button @click="dialogVisible=false">取消</el-button>
          <el-button type="primary" @click="applyExample">确认并求解</el-button>
        </div>
      </template>
    </el-dialog>
      <FormulaPanel
        :title="method==='EPI' ? '等额本息（EPI）反向求解' : '等额本金（EP）反向求解'"
        :formulas="method==='EPI' ? reverseFormulasEPI : reverseFormulasEP"
        :variables="variablesMap"
        :notes="method==='EPI' ? ['r为月利率（年化/12）','求r时使用数值法（二分）'] : ['r为月利率（年化/12）','PMT 表示首期月供，随后逐期递减']"
      />
  <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <el-form-item :label="`贷款金额（${symbol}）`" v-if="unknown!=='amount'">
            <el-input v-model.number="amountUI" type="number" placeholder="如 500000" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PV）</div>
          </el-form-item>
          <el-form-item label="年化利率（%）" v-if="unknown!=='annualRatePct'">
            <el-input v-model.number="form.annualRatePct" type="number" placeholder="如 4.2" />
            <div class="text-xs text-gray-500 mt-1">（此处填写r）</div>
          </el-form-item>
          <el-form-item label="期数（月）" v-if="unknown!=='months'">
            <el-input v-model.number="form.months" type="number" placeholder="如 360" />
            <div class="text-xs text-gray-500 mt-1">（此处填写n）</div>
          </el-form-item>
          <el-form-item :label="method==='EP' ? `月供（${symbol}，首月）` : `月供（${symbol}）`" v-if="unknown!=='pmt'">
            <el-input v-model.number="pmtUI" type="number" placeholder="如 2453.16" />
            <div class="text-xs text-gray-500 mt-1">（此处填写PMT）</div>
          </el-form-item>
        </el-form>
  <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onSolve">求解</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>

      <el-alert v-if="error" type="error" class="mt-4" :closable="false" :title="error" />

      <el-collapse-transition>
        <div v-if="solved" class="mt-4 space-y-4">
        <el-descriptions :title="`求解结果（${method==='EPI'?'等额本息':'等额本金'}）`" :column="4" border>
          <el-descriptions-item :label="`金额（${symbol}）`">{{ d2(solved.amount) }}</el-descriptions-item>
          <el-descriptions-item label="年化利率（%）">{{ solved.annualRatePct }}</el-descriptions-item>
          <el-descriptions-item label="期数（月）">{{ solved.months }}</el-descriptions-item>
          <el-descriptions-item :label="method==='EP' ? `月供（${symbol}，首月）` : `月供（${symbol}）`">{{ d2(solved.pmt) }}</el-descriptions-item>
        </el-descriptions>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <el-card shadow="never" body-class="p-0">
            <v-chart class="h-56 md:h-72" :option="chartOption" autoresize />
          </el-card>
          <el-card shadow="never" body-class="p-0">
            <el-table :data="displayRows" height="300" size="small">
              <el-table-column prop="period" label="#" width="60"/>
              <el-table-column prop="payment" label="当期还款"/>
              <el-table-column prop="principal" label="本金"/>
              <el-table-column prop="interest" label="利息"/>
              <el-table-column prop="balance" label="剩余本金"/>
            </el-table>
          </el-card>
        </div>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
  
</template>

<script setup>
import { ref, computed } from 'vue'
import { use } from 'echarts/core'
import VChart from 'vue-echarts'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { pmtEqualPI, pvFromPMT, nperFromPMT, rateFromPMT, generateSchedule, round2 } from '@/lib/loanEngine'
import { useCurrency } from '@/composables/useCurrency'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
use([CanvasRenderer, BarChart, GridComponent, TooltipComponent, LegendComponent])

const unknownOptions = [
  { label: '未知金额', value: 'amount' },
  { label: '未知利率', value: 'annualRatePct' },
  { label: '未知期数', value: 'months' },
  { label: '未知月供', value: 'pmt' }
]

const methodOptions = [
  { label: '等额本息', value: 'EPI' },
  { label: '等额本金', value: 'EP' }
]

const method = ref('EPI')

const unknown = ref('pmt')
const form = ref({ amount: 500000, annualRatePct: 4.2, months: 360, pmt: null })
const solved = ref(null)
const schedule = ref(null)
const error = ref('')

// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const amountUI = computed({
  get: () => toDisplay(form.value.amount),
  set: (v) => { form.value.amount = toBase(v) }
})
// 月供输入同样使用当前货币进行交互
const pmtUI = computed({
  get: () => toDisplay(form.value.pmt),
  set: (v) => { form.value.pmt = toBase(v) }
})

// 反向计算示例（演示用途）
const examples = ref([
  {
    key: 'budget_target_pmt',
    title: '预算月供目标，测算最高可贷金额',
    desc: `
      <ul>
        <li>目标：月供不超过 6,000 元。</li>
        <li>输入：名义年化 4.30%，期限 30 年（360 期）。</li>
        <li>未知：金额（PV）。</li>
        <li>用途：据预算反推额度上限。</li>
      </ul>
    `,
    unknown: 'amount',
    form: { amount: null, annualRatePct: 4.3, months: 360, pmt: 6000 }
  },
  {
    key: 'quota_known_pv',
    title: '额度已定，测算所需月供',
    desc: `
      <ul>
        <li>目标：额度 800,000 元（已审批）。</li>
        <li>输入：名义年化 3.95%，期限 25 年（300 期）。</li>
        <li>未知：月供（PMT）。</li>
        <li>用途：根据额度评估月度现金流。</li>
      </ul>
    `,
    unknown: 'pmt',
    form: { amount: 800000, annualRatePct: 3.95, months: 300, pmt: null }
  }
])

const dialogVisible = ref(false)
const dialogTitle = ref('')
const dialogDesc = ref('')
const exForm = ref({ amount: null, annualRatePct: null, months: null, pmt: null })
const exUnknown = ref('pmt')

const onSelectExample = (key) => {
  const ex = examples.value.find(e => e.key === key)
  if (!ex) return
  dialogTitle.value = ex.title
  dialogDesc.value = ex.desc
  exForm.value = { ...ex.form }
  exUnknown.value = ex.unknown
  dialogVisible.value = true
}

const applyExample = () => {
  unknown.value = exUnknown.value
  // exForm 内部通过 exAmountUI/exPmtUI 已存为基准币
  form.value = { ...form.value, ...exForm.value }
  dialogVisible.value = false
  onSolve()
}

// 示例弹窗金额与月供的 UI 映射（显示当前货币，存储基准货币）
const exAmountUI = computed({
  get: () => toDisplay(exForm.value.amount),
  set: (v) => { exForm.value.amount = toBase(v) }
})
const exPmtUI = computed({
  get: () => toDisplay(exForm.value.pmt),
  set: (v) => { exForm.value.pmt = toBase(v) }
})

const onSolve = () => {
  error.value = ''
  solved.value = null
  schedule.value = null
  try {
    let amount = form.value.amount
    let annualRatePct = form.value.annualRatePct
    let months = form.value.months
    let pmt = form.value.pmt
    const r = Number(annualRatePct) / 12 / 100

    if (method.value === 'EPI') {
      if (unknown.value === 'pmt') {
        if (!(amount > 0 && months > 0 && annualRatePct >= 0)) throw new Error('此处填写金额/利率/期数')
        pmt = round2(pmtEqualPI(amount, r, months))
      } else if (unknown.value === 'amount') {
        if (!(pmt > 0 && months > 0 && annualRatePct >= 0)) throw new Error('此处填写月供/利率/期数')
        amount = round2(pvFromPMT(pmt, r, months))
      } else if (unknown.value === 'months') {
        if (!(amount > 0 && pmt > 0 && annualRatePct >= 0)) throw new Error('此处填写金额/利率/月供')
        months = Math.ceil(nperFromPMT(amount, r, pmt))
      } else if (unknown.value === 'annualRatePct') {
        if (!(amount > 0 && months > 0 && pmt > 0)) throw new Error('此处填写金额/期数/月供')
        const rateMonthly = rateFromPMT(amount, months, pmt)
        annualRatePct = round2(rateMonthly * 12 * 100)
      }
      solved.value = { amount, annualRatePct, months, pmt: pmt ?? round2(pmtEqualPI(amount, Number(annualRatePct)/12/100, months)) }
    } else {
      // 等额本金（EP）——以首月月供 PMT1 作为“月供”定义
      if (unknown.value === 'pmt') {
        if (!(amount > 0 && months > 0 && annualRatePct >= 0)) throw new Error('此处填写金额/利率/期数')
        pmt = round2(amount * (r + 1 / months))
      } else if (unknown.value === 'amount') {
        if (!(pmt > 0 && months > 0 && annualRatePct >= 0)) throw new Error('此处填写月供(首月)/利率/期数')
        const denom = r + 1 / months
        if (denom <= 0) throw new Error('输入不合理，无法求解金额')
        amount = round2(pmt / denom)
      } else if (unknown.value === 'months') {
        if (!(amount > 0 && pmt > 0 && annualRatePct >= 0)) throw new Error('此处填写金额/利率/月供(首月)')
        const denom = (pmt / amount) - r
        if (denom <= 0) throw new Error('月供过低或利率过高，无法求解期数')
        months = Math.ceil(1 / denom)
      } else if (unknown.value === 'annualRatePct') {
        if (!(amount > 0 && months > 0 && pmt > 0)) throw new Error('此处填写金额/期数/月供(首月)')
        const r_m = (pmt / amount) - 1 / months
        if (r_m < 0) throw new Error('输入不合理，利率不能为负')
        annualRatePct = round2(r_m * 12 * 100)
      }
      // 确保展示首期月供
      pmt = pmt ?? round2(amount * (Number(annualRatePct)/12/100 + 1 / months))
      solved.value = { amount, annualRatePct, months, pmt }
    }

    schedule.value = generateSchedule({ amount, annualRatePct, months, method: method.value })
  } catch (e) {
    error.value = e?.message || '求解失败'
  }
}

const onReset = () => {
  unknown.value = 'pmt'
  form.value = { amount: 0, annualRatePct: 0, months: 0, pmt: null }
  solved.value = null
  schedule.value = null
  error.value = ''
}

const chartOption = computed(() => {
  if (!schedule.value) return {}
  const periods = schedule.value.rows.map(r => r.period)
  const principals = schedule.value.rows.map(r => toDisplay(r.principal))
  const interests = schedule.value.rows.map(r => toDisplay(r.interest))
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['本金', '利息'] },
    grid: { left: 24, right: 16, top: 24, bottom: 24, containLabel: true },
    xAxis: { type: 'category', data: periods },
    yAxis: { type: 'value' },
    series: [
      { name: '本金', type: 'bar', stack: 'total', data: principals },
      { name: '利息', type: 'bar', stack: 'total', data: interests },
    ]
  }
})

const displayRows = computed(() => {
  if (!schedule.value) return []
  return schedule.value.rows.map(r => ({
    ...r,
    payment: Number(toDisplay(r.payment)).toFixed(2),
    principal: Number(toDisplay(r.principal)).toFixed(2),
    interest: Number(toDisplay(r.interest)).toFixed(2),
    balance: Number(toDisplay(r.balance)).toFixed(2)
  }))
})

const reverseFormulasEPI = [
  String.raw`\text{PMT} = \dfrac{PV \cdot r_m (1+r_m)^n}{(1+r_m)^n - 1}`,
  String.raw`PV = \dfrac{\text{PMT} \left((1+r_m)^n - 1\right)}{r_m (1+r_m)^n}`,
  String.raw`n = \dfrac{\ln\left(\dfrac{\text{PMT}}{\text{PMT} - PV\cdot r_m}\right)}{\ln(1+r_m)}`
]
const reverseFormulasEP = [
  String.raw`\text{PMT}_{1} = PV\cdot\left(r_m + \dfrac{1}{n}\right)`,
  String.raw`PV = \dfrac{\text{PMT}_{1}}{r_m + 1/n}`,
  String.raw`n = \left\lceil \dfrac{1}{\text{PMT}_{1}/PV - r_m} \right\rceil`,
  String.raw`r_m = \text{PMT}_{1}/PV - 1/n`
]
const variablesMap = computed(() => ([
  { symbol: 'PV', label: `贷款金额（${symbol.value}）` },
  { symbol: 'r_m', label: '月利率', desc: '年化/12' },
  { symbol: 'n', label: '期数（月）' },
  { symbol: 'PMT', label: `月供（${symbol.value}）` },
]))

// 显示用：将基准货币金额转换为当前货币并保留两位小数
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

</script>
<style scoped>
.mobile-wrap :deep(.el-segmented__group) {
  flex-wrap: wrap;
  row-gap: 6px;
}
.EPI{
  --el-segmented-item-selected-color: var(--el-text-color-primary);
  --el-segmented-item-selected-bg-color: #ffd100;
  --el-border-radius-base: 16px;
}
</style>